/**
 * NextDropsPreview — surfaces 3-4 upcoming tracks inside /vault so members
 * see the drumbeat of new drops without leaving. The old /roadmap page was
 * cleared last session (redirects to /vault); this component IS the new
 * roadmap, embedded inline. Entries already returned by the live categories
 * API are automatically removed so shipped work is never teased as upcoming.
 */
import { Sparkles, Calendar } from "lucide-react";

const UPCOMING_DROPS = [
  {
    n: 101,
    slug: "ai-wedding-photo-album-studio",
    name: "AI Wedding-Photo Album Studio",
    tagline: "Auto-cull, colour-grade and album-layout AI for wedding photographers → £199-499/wedding.",
    eta: "Next drop",
    accent: "purple",
  },
  {
    n: 102,
    slug: "ai-estate-agent-listing-rescue",
    name: "AI Estate-Agent Listing Rescue",
    tagline: "AI-rewritten listings + hero shots that lift viewings → £99/listing + £299/mo agent retainer.",
    eta: "Sep 2026",
    accent: "purple",
  },
  {
    n: 103,
    slug: "ai-salon-barber-content-engine",
    name: "AI Salon & Barber Content Engine",
    tagline: "Instagram + Google-review + booking-nudge automations for hair & beauty → £299/mo.",
    eta: "Sep 2026",
    accent: "purple",
  },
  {
    n: 104,
    slug: "ai-airbnb-superhost-guest-guides",
    name: "AI Airbnb Superhost Guest-Guides",
    tagline: "Personalised welcome-guide + local-tips packs auto-generated per booking → £29/mo per property.",
    eta: "Oct 2026",
    accent: "amber",
  },
];

export function NextDropsPreview({ categories, loading }) {
  if (loading || !Array.isArray(categories) || categories.length === 0) return null;

  const liveTrackNumbers = new Set(categories.map((category) => Number(category.order)));
  const liveSlugs = new Set(categories.map((category) => category.slug).filter(Boolean));
  const unshippedDrops = UPCOMING_DROPS.filter(
    (drop) => !liveTrackNumbers.has(drop.n) && !liveSlugs.has(drop.slug),
  );

  if (unshippedDrops.length === 0) return null;

  return (
    <section
      className="mt-14 md:mt-20 border-t border-purple-500/15 pt-10 md:pt-14"
      data-testid="next-drops-preview"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6 md:mb-8">
        <div>
          <div className="text-purple-400 text-xs md:text-sm tracking-[0.35em] uppercase mb-3 font-mono inline-flex items-center gap-2">
            <Sparkles size={12} strokeWidth={2.5} />
            Next drops · the roadmap
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            Coming next to the vault.
          </h2>
          <p className="mt-3 text-zinc-400 text-base md:text-lg max-w-xl">
            Every new track is included in Lifetime — no upsells, no upgrade emails. Just more ways to build something people will pay for.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900">
        {unshippedDrops.map((t) => (
          <article
            key={t.n}
            className="bg-[#0a0a0a] p-6 md:p-7 relative flex flex-col"
            data-testid={`next-drop-${t.n}`}
          >
            <div className="flex items-start justify-between mb-5">
              <span className="text-zinc-500 text-xs tracking-[0.2em] uppercase font-mono">
                Track {t.n}
              </span>
              <span
                className={`inline-flex items-center gap-1 border ${
                  t.accent === "amber"
                    ? "border-amber-400/60 text-amber-200 bg-amber-500/[0.06]"
                    : "border-purple-500/50 text-purple-200 bg-purple-500/[0.06]"
                } text-[9px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold`}
              >
                <Calendar size={10} strokeWidth={2.5} />
                {t.eta}
              </span>
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight mb-3">
              {t.name}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed flex-1">{t.tagline}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
